import { ApiError, type ApiClient } from "./types";
import { REGION_IDS_PARAM, appendRegionIds } from "./modules/regions/params";

/**
 * Adds the app's active region selection to a request path.
 *
 * This is the single place region scoping is applied, so every GET the app
 * already makes — all 50-odd list modules — carries it without its own call
 * site being rewritten. Deliberately narrow:
 *
 * - **Reads only.** A region scopes what you look at; silently narrowing a
 *   write by the sidebar's current selection would be a surprise.
 * - **Silent for "all regions".** The default selection emits no param, so a
 *   fleet with no regions sends exactly the request it sends today.
 * - **A call site that passed its own `region_ids` wins**, so an explicit
 *   report or picker can ask for a region other than the selected one.
 * - **Never fails the request.** The reader is app state; a bug in it must
 *   not take down every read in the app.
 */
function withRegionScope(
  apiClient: ApiClient,
  path: string,
  options: RequestInit,
): string {
  const method = (options.method ?? "GET").toUpperCase();
  if (method !== "GET") return path;
  if (!apiClient.getRegionIds) return path;
  if (path.includes(REGION_IDS_PARAM) || path.includes("region_ids")) {
    return path;
  }

  let regionIds: readonly string[] | null | undefined;
  try {
    regionIds = apiClient.getRegionIds();
  } catch {
    return path;
  }

  const params = appendRegionIds(new URLSearchParams(), regionIds);
  const query = params.toString();
  if (!query) return path;

  return `${path}${path.includes("?") ? "&" : "?"}${query}`;
}

/**
 * Fetches a file rather than a document.
 *
 * `clientWithApiClient` falls back to `res.text()` for anything that is not
 * JSON, which decodes a PDF's bytes as UTF-8 and silently corrupts the file —
 * the download saves, opens to an error, and nothing anywhere reported a
 * failure. This reads the body as a `Blob` and leaves the bytes alone.
 *
 * Auth and error handling are deliberately the same shape as the JSON client:
 * a refusal must raise, or the caller saves the error page under a `.pdf`
 * name.
 */
export async function blobWithApiClient(
  apiClient: ApiClient,
  path: string,
  options: RequestInit = {},
): Promise<{ blob: Blob; filename: string | null }> {
  let authToken: string | null = null;
  if (apiClient.getAuthToken) {
    authToken = await apiClient.getAuthToken();
  }

  if (!authToken && apiClient.apiKey) {
    authToken = apiClient.apiKey;
  }

  const res = await fetch(
    `${apiClient.baseUrl}${withRegionScope(apiClient, path, options)}`,
    {
      ...options,
      headers: authToken
        ? { ...(options.headers || {}), Authorization: `Bearer ${authToken}` }
        : options.headers,
    },
  );

  if (!res.ok) {
    const errorText = await res.text();

    try {
      const errorJson = JSON.parse(errorText);
      const message =
        errorJson.error ||
        errorJson.message ||
        res.statusText ||
        "Request failed";
      throw new ApiError(
        res.status,
        typeof message === "object" ? JSON.stringify(message) : message,
        errorJson,
      );
    } catch (parseError) {
      if (parseError instanceof ApiError) throw parseError;
      throw new ApiError(res.status, res.statusText || "Request failed", {
        raw: errorText,
      });
    }
  }

  return {
    blob: await res.blob(),
    filename: filenameFromDisposition(res.headers.get("content-disposition")),
  };
}

function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;

  const match =
    /filename\*=UTF-8''([^;]+)/i.exec(disposition) ||
    /filename="([^"]+)"/i.exec(disposition) ||
    /filename=([^;]+)/i.exec(disposition);

  if (!match?.[1]) return null;

  // Only the last path segment, and no separators: a server that echoed a
  // name back into `Content-Disposition` must not be able to steer where the
  // browser writes.
  const name = decodeURIComponent(match[1].trim()).split(/[\\/]/).pop();

  return name || null;
}

export async function clientWithApiClient<T = any>(
  apiClient: ApiClient,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const baseUrl = apiClient.baseUrl;
  let authToken: string | null = null;
  if (apiClient.getAuthToken) {
    authToken = await apiClient.getAuthToken();
  }

  if (!authToken && apiClient.apiKey) {
    authToken = apiClient.apiKey;
  }

  // Don't set Content-Type for FormData (browser will set it with boundary)
  const baseHeaders =
    options.body instanceof FormData
      ? options.headers
      : {
          "Content-Type": "application/json",
          ...(options.headers || {}),
        };

  // Add Authorization header if token is available
  const headers = authToken
    ? { ...baseHeaders, Authorization: `Bearer ${authToken}` }
    : baseHeaders;

  const fullUrl = `${baseUrl}${withRegionScope(apiClient, path, options)}`;

  const res = await fetch(fullUrl, {
    ...options,
    headers,
  });

  if (res.status === 204) {
    return {} as T;
  }

  if (res.status === 422) {
    const errorText = await res.text();
    try {
      const errorJson = JSON.parse(errorText);
      let errorMessage =
        errorJson.error ||
        errorJson.message ||
        errorJson.errors ||
        "Unprocessable Entity";
      if (typeof errorMessage === "object" && errorMessage !== null) {
        errorMessage = errorMessage.message || JSON.stringify(errorMessage);
      }
      throw new ApiError(res.status, errorMessage, errorJson);
    } catch (parseError) {
      if (parseError instanceof ApiError) throw parseError;
      throw new ApiError(res.status, "Unprocessable Entity", {});
    }
  }

  if (!res.ok) {
    const errorText = await res.text();

    // Try to parse as JSON first
    try {
      const errorJson = JSON.parse(errorText);

      // Extract message, handling cases where it might be an object
      let message =
        errorJson.error ||
        errorJson.message ||
        res.statusText ||
        "Request failed";

      // If message is an object, stringify it
      if (typeof message === "object" && message !== null) {
        message = JSON.stringify(message);
      }

      throw new ApiError(res.status, message, errorJson);
    } catch (parseError) {
      // If it's already an ApiError, rethrow it
      if (parseError instanceof ApiError) {
        throw parseError;
      }

      // Not valid JSON, try to extract from HTML title
      const titleMatch = errorText.match(/<title>(.*?)<\/title>/i);
      const message = titleMatch?.[1] || res.statusText || "Request failed";
      throw new ApiError(res.status, message, { raw: errorText });
    }
  }

  if (res.headers.get("content-type")?.includes("application/json")) {
    const jsonData = await res.json();

    return jsonData as T;
  } else {
    return res.text() as unknown as T;
  }
}

export { fleetWide } from "./fleet-wide";
